/* eslint-disable @typescript-eslint/no-explicit-any */
import { dbGetAll, dbGet, dbPut, kvGet, TStore } from './db';

// Consultas nos cadastros guardados localmente — mesmas regras das
// consultas do servidor (ver ProdutosProvaider.getAll, ParceirosProvaider
// etc. no backend): sem diferenciar acento/maiúscula, "começa com" ou
// "contém", ordenado pelo mesmo campo e paginado igual, pra que as telas
// recebam exatamente o mesmo formato online e offline.

export const normalizar = (texto: unknown): string =>
  String(texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const bate = (valor: unknown, filtro: string, modo: 'inicio' | 'contem') => {
  const alvo = normalizar(valor);
  return modo === 'inicio' ? alvo.startsWith(filtro) : alvo.includes(filtro);
};

const porCampo = (campo: string) => (a: any, b: any) =>
  String(a[campo] ?? '').localeCompare(String(b[campo] ?? ''), 'pt-BR');

const paginar = <T>(lista: T[], pagina: number, limite: number) => ({
  data: lista.slice((pagina - 1) * limite, pagina * limite),
  totalCount: lista.length,
});

// Os catálogos ficam em memória depois da primeira leitura — a busca de
// produto roda a cada tecla, e ler dezenas de milhares de linhas do
// IndexedDB toda vez seria lento demais. A sincronização invalida esse
// cache (ver invalidarCache) quando baixa uma cópia nova.
const memoria: Partial<Record<TStore, Promise<any[]>>> = {};

const carregar = (store: TStore): Promise<any[]> => {
  if (!memoria[store]) {
    memoria[store] = dbGetAll(store).catch(() => {
      delete memoria[store];
      return [];
    });
  }
  return memoria[store] as Promise<any[]>;
};

export const invalidarCache = (store?: TStore) => {
  if (store) delete memoria[store];
  else (Object.keys(memoria) as TStore[]).forEach((chave) => delete memoria[chave]);
};

// ---------- Produtos ----------

export const buscarProdutos = async (
  pagina: number, filtro: string, tipo: string, limite: number, modobusca: string,
) => {
  const termo = normalizar(filtro);
  const modo = modobusca === 'inicio' ? 'inicio' : 'contem';

  if (tipo === 'referencia') {
    const referencias = await carregar('referencias');
    const lista = referencias.filter((r) => bate(r.referencia, termo, modo)).sort(porCampo('descricao'));
    return paginar(lista, pagina, limite);
  }

  const campo = tipo === 'codinterno' ? 'codinterno' : tipo === 'codbarra' ? 'ean' : 'descricao';
  if (!['descricao', 'codinterno', 'codbarra'].includes(tipo)) return { data: [], totalCount: 0 };
  const produtos = await carregar('produtos');
  const lista = produtos.filter((p) => bate(p[campo], termo, modo)).sort(porCampo('descricao'));
  return paginar(lista, pagina, limite);
};

export const buscarProdutoPorEan = async (ean: string) => {
  const produtos = await carregar('produtos');
  const produto = produtos.find((p) => String(p.ean ?? '') === ean);
  return produto ? { ...produto, codbarraentrada: produto.ean } : undefined;
};

export const buscarProdutoPorId = async (idproduto: string) => {
  const produtos = await carregar('produtos');
  return produtos.find((p) => p.idproduto === idproduto);
};

// Baixa o estoque local depois de uma venda offline — só pra a quantidade
// mostrada na busca não ficar parada até a próxima sincronização (que
// traz o valor real do servidor e sobrescreve este).
export const baixarEstoqueLocal = async (itens: { idprodutos: string; qtds: number }[]) => {
  const produtos = await carregar('produtos');
  const referencias = await carregar('referencias');
  for (const item of itens) {
    const qtd = Number(item.qtds) || 0;
    const produto = produtos.find((p) => p.idproduto === item.idprodutos);
    if (produto) {
      produto.quan = Number(produto.quan || 0) - qtd;
      await dbPut('produtos', produto).catch(() => undefined);
    }
    referencias
      .filter((r) => r.idproduto === item.idprodutos)
      .forEach((r) => { r.quan = Number(r.quan || 0) - qtd; });
  }
};

// ---------- Parceiros ----------

export const buscarParceiros = async (filtro: string, id: string, pagina: number, limite: number) => {
  const parceiros = await carregar('parceiros');
  if (id) {
    return { data: parceiros.filter((p) => p.idparceiro === id), totalCount: 1 };
  }
  const termo = normalizar(filtro);
  const termoDigitos = filtro.replace(/\D/g, '');
  const lista = parceiros
    .filter((p) => bate(p.nome, termo, 'contem')
      || (termoDigitos.length >= 3 && String(p.cpf ?? '').replace(/\D/g, '').includes(termoDigitos)))
    .sort(porCampo('nome'));
  return paginar(lista, pagina, limite);
};

export const buscarParceiroPorId = async (idparceiro: string) => {
  const parceiros = await carregar('parceiros');
  return parceiros.find((p) => p.idparceiro === idparceiro);
};

export const adicionarParceiroLocal = async (parceiro: any) => {
  await dbPut('parceiros', parceiro);
  const parceiros = await carregar('parceiros');
  const indice = parceiros.findIndex((p) => p.idparceiro === parceiro.idparceiro);
  if (indice >= 0) parceiros[indice] = parceiro;
  else parceiros.push(parceiro);
};

// ---------- Plano de contas ----------

export const buscarPlanoContas = async (filtro: string, id: string, limite: number) => {
  const planos = await carregar('planocontas');
  if (id) {
    const lista = planos.filter((p) => p.idplanoconta === id);
    return { data: lista, totalCount: lista.length };
  }
  const termo = normalizar(filtro);
  const lista = planos.filter((p) => bate(p.descricao, termo, 'contem')).sort(porCampo('grupo'));
  return paginar(lista, 1, limite);
};

// ---------- Operações ----------

export const buscarOperacoes = async (filtro: string, id: string, tipo: number, limite: number) => {
  const operacoes = await carregar('operacoes');
  if (id) {
    const lista = operacoes.filter((o) => o.idoperacoes === id);
    return { data: lista, totalCount: lista.length };
  }
  const termo = normalizar(filtro);
  const lista = operacoes
    .filter((o) => o.status !== false)
    .filter((o) => Number(tipo) === 3 || Number(o.tipo) === Number(tipo))
    .filter((o) => bate(o.descricao, termo, 'contem'))
    .sort(porCampo('descricao'));
  return paginar(lista, 1, limite);
};

export const buscarOperacaoPorId = async (idoperacoes: string) => dbGet('operacoes', idoperacoes);

// ---------- Contas bancárias ----------

export const buscarContas = async (filtro: string, id: string, tipo: string, limite: number) => {
  const contas = await carregar('contas');
  if (id) {
    const lista = contas.filter((c) => c.idconta === id);
    return { data: lista, totalCount: lista.length };
  }
  const termo = normalizar(filtro);
  const tipos = tipo ? tipo.split(',') : null;
  const lista = contas
    .filter((c) => !tipos || tipos.includes(c.tipo))
    .filter((c) => bate(c.numeroconta, termo, 'contem') || bate(c.titular, termo, 'contem') || bate(c.agencia, termo, 'contem'))
    .sort(porCampo('titular'));
  return paginar(lista, 1, limite);
};

// ---------- Vendedores ----------

export const buscarVendedores = async (filtro: string, id: string, limite: number) => {
  const vendedores = await carregar('vendedores');
  if (id) {
    const lista = vendedores.filter((v) => v.iduser === id);
    return { data: lista, totalCount: lista.length };
  }
  const termo = normalizar(filtro);
  const lista = vendedores.filter((v) => bate(v.nome, termo, 'contem')).sort(porCampo('nome'));
  return paginar(lista, 1, limite);
};

// ---------- Estoque ----------

// Mesma resposta de POST /produtos/verificarestoque: só os itens sem saldo
// suficiente, pelo estoque da cópia local (atualizado a cada sincronização
// e baixado a cada venda offline).
export const verificarEstoqueLocal = async (itens: { idprodutos: string; qtde: number }[]) => {
  const produtos = await carregar('produtos');
  const solicitado = new Map<string, number>();
  itens.forEach((item) => solicitado.set(item.idprodutos, (solicitado.get(item.idprodutos) || 0) + (Number(item.qtde) || 0)));
  const faltando: { idprodutos: string; descricao: string; qtdesolicitada: number; estoquedisponivel: number }[] = [];
  solicitado.forEach((qtde, idprodutos) => {
    const produto = produtos.find((p) => p.idproduto === idprodutos);
    const disponivel = Number(produto?.quan || 0);
    if (qtde > disponivel) {
      faltando.push({ idprodutos, descricao: produto?.descricao || '', qtdesolicitada: qtde, estoquedisponivel: disponivel });
    }
  });
  return faltando;
};

// ---------- Detalhe de produto (FrontSIAC, só leitura) ----------

// Só devolve o produto se a sincronização trouxe o cadastro completo dele
// (ver /produtosoffline em sincronizacao.ts) — a linha da listagem sozinha
// não tem grupo, dados fiscais etc.
export const buscarProdutoDetalhe = async (idproduto: string) => {
  const produto = await buscarProdutoPorId(idproduto);
  return produto && 'idgrupo' in produto ? produto : undefined;
};

const tabelaKv = async (chave: string): Promise<any[]> => (await kvGet<any[]>(chave)) || [];

export const buscarGrupos = async (filtro: string, id: string, limite: number) => {
  const grupos = await tabelaKv('grupos');
  if (id) {
    const lista = grupos.filter((g) => g.idgrupo === id);
    return { data: lista, totalCount: lista.length };
  }
  const termo = normalizar(filtro);
  return paginar(grupos.filter((g) => bate(g.descricao, termo, 'contem')).sort(porCampo('descricao')), 1, limite);
};

export const buscarSubgrupos = async (idgrupo: string, filtro: string, id: string, limite: number) => {
  const subgrupos = await tabelaKv('subgrupos');
  if (id) {
    const lista = subgrupos.filter((s) => s.idsubgrupo === id);
    return { data: lista, totalCount: lista.length };
  }
  const termo = normalizar(filtro);
  const lista = subgrupos
    .filter((s) => s.idgrupo === idgrupo && bate(s.descricao, termo, 'contem'))
    .sort(porCampo('descricao'));
  return paginar(lista, 1, limite);
};

// Sem filtro: a lista toda (paginada); com filtro: pelo código ou natureza.
export const buscarCfop = async (filtro: string, limite: number) => {
  const cfops = await tabelaKv('cfop');
  const termo = normalizar(filtro);
  const lista = termo
    ? cfops.filter((c) => String(c.cfop).startsWith(filtro.trim()) || bate(c.natureza, termo, 'contem'))
    : cfops;
  return paginar(lista, 1, termo ? lista.length || 1 : limite);
};
